/**
 * useTreeTestStorage (TASK-260)
 *
 * Per-user localStorage persistence of the LAST manual tree-test run
 * (selection + inputs + result). Exactly ONE slot per user under the key
 * `tree-test:<userId>` (see `@/services/treeTestStorage`).
 *
 * Contract (FR-005 / AC-5):
 *   read(validServiceIds?) — returns the stored state, or null when the slot is
 *     missing/unparseable. When `validServiceIds` is provided, selections whose
 *     `service_id` is NOT in that list are softly dropped (one console.warn per
 *     dropped id) so a stale slot after a reindex degrades gracefully instead of
 *     crashing. When omitted → no validation.
 *   save(state) — overwrites the user's single slot.
 *   clear() — removes the slot.
 */
import { useCallback } from 'react';

import type { TreeTestStoredState } from '@/services/api/services';
import {
  treeTestStorageKey,
  clearTreeTestSlot,
} from '@/services/treeTestStorage';

export interface UseTreeTestStorage {
  read(validServiceIds?: number[]): TreeTestStoredState | null;
  save(state: TreeTestStoredState): void;
  clear(): void;
}

export function useTreeTestStorage(userId: string): UseTreeTestStorage {
  const read = useCallback(
    (validServiceIds?: number[]): TreeTestStoredState | null => {
      let raw: string | null = null;
      try {
        raw = localStorage.getItem(treeTestStorageKey(userId));
      } catch {
        return null;
      }
      if (raw === null) return null;

      let parsed: TreeTestStoredState;
      try {
        parsed = JSON.parse(raw) as TreeTestStoredState;
      } catch {
        // Corrupt slot → treat as absent (graceful degradation).
        return null;
      }

      // No validation requested → return the slot untouched.
      if (!validServiceIds) return parsed;

      const valid = new Set(validServiceIds);
      const selections = parsed.request?.selections ?? [];
      const kept = selections.filter((sel) => {
        if (valid.has(sel.service_id)) return true;
        // Stale service_id (e.g. removed by a reindex) — drop softly + warn.
        console.warn(
          `[useTreeTestStorage] dropping stale service_id ${sel.service_id} ` +
            'not present in the current tree'
        );
        return false;
      });

      return {
        ...parsed,
        request: { ...parsed.request, selections: kept },
      };
    },
    [userId]
  );

  const save = useCallback(
    (state: TreeTestStoredState): void => {
      try {
        localStorage.setItem(
          treeTestStorageKey(userId),
          JSON.stringify(state)
        );
      } catch {
        // localStorage unavailable / quota exceeded — fail silently.
      }
    },
    [userId]
  );

  const clear = useCallback((): void => {
    clearTreeTestSlot(userId);
  }, [userId]);

  return { read, save, clear };
}

export default useTreeTestStorage;
